/**
 * AddJobsWizard Component
 *
 * Multi-step wizard for batch creating jobs for units in a project.
 * Steps:
 * 1. Select permit type and trade
 * 2. Select units to create jobs for
 * 3. (Optional) Select template job to copy from
 * 4. Review and create
 */

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useBatchCreateJobs, useProjectJobs, type BatchCreateJobsInput } from '@/hooks/useProject';
import { usePermitTypes, useJobScopes } from '@/hooks/useCompliance';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  FileText,
  Grid3X3,
  Copy,
  AlertCircle,
} from 'lucide-react';
import type { Unit, JobScopeType } from '@/types/project';

interface AddJobsWizardProps {
  projectId: string;
  projectName: string;
  units: Unit[];
  onComplete: (jobIds: string[]) => void;
  onCancel: () => void;
}

type WizardStep = 'permit' | 'units' | 'template' | 'review';

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'permit', label: 'Permit Type' },
  { key: 'units', label: 'Select Units' },
  { key: 'template', label: 'Template' },
  { key: 'review', label: 'Review' },
];

// Common permit types for quick selection
const COMMON_PERMIT_TYPES = [
  { code: 'building', label: 'Building' },
  { code: 'electrical', label: 'Electrical' },
  { code: 'plumbing', label: 'Plumbing' },
  { code: 'mechanical', label: 'Mechanical/HVAC' },
  { code: 'roofing', label: 'Roofing' },
  { code: 'gas', label: 'Gas' },
];

export function AddJobsWizard({
  projectId,
  projectName,
  units,
  onComplete,
  onCancel,
}: AddJobsWizardProps) {
  const { toast } = useToast();
  const { batchCreateJobs, isLoading: isCreating } = useBatchCreateJobs();
  const { jobs: existingJobs } = useProjectJobs(projectId);

  // Step state
  const [currentStep, setCurrentStep] = useState<WizardStep>('permit');

  // Form state
  const [permitType, setPermitType] = useState<string>('');
  const [tradeType, setTradeType] = useState<string>('');
  const [jobScopeType, setJobScopeType] = useState<'unit_specific' | 'shared_common'>('unit_specific');
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [useTemplate, setUseTemplate] = useState(false);
  const [templateJobId, setTemplateJobId] = useState<string>('');
  const [customTitle, setCustomTitle] = useState<string>('');
  const [customDescription, setCustomDescription] = useState<string>('');

  // Get current step index
  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);
  const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

  // Filter template candidates (existing jobs that can be copied)
  const templateCandidates = useMemo(() => {
    return existingJobs.filter((job) => {
      // Match permit type if selected
      if (permitType && job.permitType !== permitType) return false;
      return true;
    });
  }, [existingJobs, permitType]);

  // Validation
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 'permit':
        return permitType.length > 0;
      case 'units':
        return jobScopeType === 'shared_common' || selectedUnitIds.length > 0;
      case 'template':
        return true; // Optional step
      case 'review':
        return true;
      default:
        return false;
    }
  }, [currentStep, permitType, jobScopeType, selectedUnitIds]);

  // Navigation
  const goNext = () => {
    const idx = STEPS.findIndex((s) => s.key === currentStep);
    if (idx < STEPS.length - 1) {
      // Skip units step for shared_common
      if (currentStep === 'permit' && jobScopeType === 'shared_common') {
        setCurrentStep('template');
      } else {
        setCurrentStep(STEPS[idx + 1].key);
      }
    }
  };

  const goBack = () => {
    const idx = STEPS.findIndex((s) => s.key === currentStep);
    if (idx > 0) {
      // Skip units step for shared_common when going back
      if (currentStep === 'template' && jobScopeType === 'shared_common') {
        setCurrentStep('permit');
      } else {
        setCurrentStep(STEPS[idx - 1].key);
      }
    }
  };

  // Toggle unit selection
  const toggleUnit = (unitId: string) => {
    setSelectedUnitIds((prev) =>
      prev.includes(unitId)
        ? prev.filter((id) => id !== unitId)
        : [...prev, unitId]
    );
  };

  // Select/deselect all units
  const toggleAllUnits = () => {
    if (selectedUnitIds.length === units.length) {
      setSelectedUnitIds([]);
    } else {
      setSelectedUnitIds(units.map((u) => u.id));
    }
  };

  // Submit
  const handleSubmit = async () => {
    try {
      const input: BatchCreateJobsInput = {
        projectId,
        unitIds: jobScopeType === 'shared_common' ? ['shared'] : selectedUnitIds,
        permitType,
        tradeType: tradeType || undefined,
        jobScopeType,
        templateJobId: useTemplate && templateJobId ? templateJobId : undefined,
        title: customTitle || undefined,
        description: customDescription || undefined,
      };

      const result = await batchCreateJobs(input);

      toast({
        title: 'Jobs created',
        description: `Successfully created ${result.count} job${result.count !== 1 ? 's' : ''}`,
      });

      onComplete(result.jobIds);
    } catch (err) {
      console.error('Error creating jobs:', err);
      toast({
        title: 'Error',
        description: 'Failed to create jobs. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold">Add Jobs to {projectName}</h2>
        <p className="text-muted-foreground">
          Create permit jobs for multiple units at once
        </p>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>{STEPS[currentStepIndex].label}</span>
          <span>
            Step {currentStepIndex + 1} of {STEPS.length}
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Permit Type */}
          {currentStep === 'permit' && (
            <div className="space-y-6">
              <div>
                <Label className="text-base font-medium">Job Scope</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Choose whether this is for individual units or shared across the project
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className={`p-4 border rounded-lg text-left transition-colors ${
                      jobScopeType === 'unit_specific'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setJobScopeType('unit_specific')}
                  >
                    <Grid3X3 className="h-5 w-5 mb-2" />
                    <p className="font-medium">Unit-Specific</p>
                    <p className="text-sm text-muted-foreground">
                      Create a job for each selected unit
                    </p>
                  </button>
                  <button
                    type="button"
                    className={`p-4 border rounded-lg text-left transition-colors ${
                      jobScopeType === 'shared_common'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setJobScopeType('shared_common')}
                  >
                    <FileText className="h-5 w-5 mb-2" />
                    <p className="font-medium">Shared/Common</p>
                    <p className="text-sm text-muted-foreground">
                      Single permit for entire project
                    </p>
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-base font-medium">Permit Type *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Select the type of permit to create
                </p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {COMMON_PERMIT_TYPES.map((pt) => (
                    <button
                      key={pt.code}
                      type="button"
                      className={`p-2 border rounded-lg text-sm transition-colors ${
                        permitType === pt.code
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setPermitType(pt.code)}
                    >
                      {pt.label}
                    </button>
                  ))}
                </div>
                <div>
                  <Label htmlFor="custom-permit" className="text-sm">
                    Or enter custom permit type
                  </Label>
                  <Input
                    id="custom-permit"
                    placeholder="e.g., Fire Sprinkler"
                    value={permitType}
                    onChange={(e) => setPermitType(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="trade-type">Trade (Optional)</Label>
                <Input
                  id="trade-type"
                  placeholder="e.g., electrical, plumbing"
                  value={tradeType}
                  onChange={(e) => setTradeType(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 2: Select Units */}
          {currentStep === 'units' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Select Units</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose which units to create {permitType} jobs for
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleAllUnits}
                >
                  {selectedUnitIds.length === units.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                {units.map((unit) => {
                  const isSelected = selectedUnitIds.includes(unit.id);
                  return (
                    <div
                      key={unit.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 ${
                        isSelected ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => toggleUnit(unit.id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleUnit(unit.id)}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{unit.label}</p>
                        <p className="text-sm text-muted-foreground">
                          {unit.unitType} • {unit.jobCount || 0} existing jobs
                        </p>
                      </div>
                      <Badge variant="secondary">{unit.status}</Badge>
                    </div>
                  );
                })}
              </div>

              <p className="text-sm text-muted-foreground">
                {selectedUnitIds.length} of {units.length} units selected
              </p>
            </div>
          )}

          {/* Step 3: Template */}
          {currentStep === 'template' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="use-template"
                  checked={useTemplate}
                  onCheckedChange={(checked) => setUseTemplate(checked as boolean)}
                />
                <Label htmlFor="use-template" className="text-base font-medium cursor-pointer">
                  Copy from existing job
                </Label>
              </div>

              {useTemplate && (
                <div className="space-y-4 pl-6">
                  {templateCandidates.length === 0 ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <AlertCircle className="h-4 w-4" />
                      <span>No matching jobs found to use as template</span>
                    </div>
                  ) : (
                    <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                      {templateCandidates.map((job) => (
                        <div
                          key={job.id}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 ${
                            templateJobId === job.id ? 'bg-primary/5' : ''
                          }`}
                          onClick={() => setTemplateJobId(job.id)}
                        >
                          <input
                            type="radio"
                            name="template"
                            checked={templateJobId === job.id}
                            onChange={() => setTemplateJobId(job.id)}
                            className="h-4 w-4"
                          />
                          <div className="flex-1">
                            <p className="font-medium">{job.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {job.permitType || 'No permit type'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 border-t">
                <Label htmlFor="custom-title">Custom Job Title (Optional)</Label>
                <Input
                  id="custom-title"
                  placeholder="Leave blank for auto-generated title"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="custom-description">Description (Optional)</Label>
                <Textarea
                  id="custom-description"
                  placeholder="Add notes or description for all created jobs"
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 'review' && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium">Review Your Selection</h4>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Permit Type</p>
                    <p className="font-medium">{permitType}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Job Scope</p>
                    <p className="font-medium">
                      {jobScopeType === 'shared_common' ? 'Shared/Common' : 'Unit-Specific'}
                    </p>
                  </div>
                  {tradeType && (
                    <div>
                      <p className="text-muted-foreground">Trade</p>
                      <p className="font-medium">{tradeType}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Jobs to Create</p>
                    <p className="font-medium">
                      {jobScopeType === 'shared_common' ? 1 : selectedUnitIds.length}
                    </p>
                  </div>
                </div>

                {jobScopeType === 'unit_specific' && selectedUnitIds.length > 0 && (
                  <div>
                    <p className="text-muted-foreground text-sm mb-2">Selected Units:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedUnitIds.slice(0, 10).map((unitId) => {
                        const unit = units.find((u) => u.id === unitId);
                        return (
                          <Badge key={unitId} variant="secondary">
                            {unit?.label || unitId}
                          </Badge>
                        );
                      })}
                      {selectedUnitIds.length > 10 && (
                        <Badge variant="outline">
                          +{selectedUnitIds.length - 10} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {useTemplate && templateJobId && (
                  <div>
                    <p className="text-muted-foreground text-sm">Template:</p>
                    <div className="flex items-center gap-2">
                      <Copy className="h-4 w-4" />
                      <span>
                        {templateCandidates.find((j) => j.id === templateJobId)?.title || templateJobId}
                      </span>
                    </div>
                  </div>
                )}

                {customTitle && (
                  <div>
                    <p className="text-muted-foreground text-sm">Custom Title:</p>
                    <p>{customTitle}</p>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Note:</strong> This will create{' '}
                  {jobScopeType === 'shared_common' ? '1 job' : `${selectedUnitIds.length} jobs`}{' '}
                  for the {projectName} project. You can edit individual jobs after creation.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={currentStepIndex === 0 ? onCancel : goBack}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {currentStepIndex === 0 ? 'Cancel' : 'Back'}
        </Button>

        {currentStep === 'review' ? (
          <Button onClick={handleSubmit} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Create{' '}
                {jobScopeType === 'shared_common' ? '1 Job' : `${selectedUnitIds.length} Jobs`}
              </>
            )}
          </Button>
        ) : (
          <Button onClick={goNext} disabled={!canProceed}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
